import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DO_ESTADO,
  useAgentesDeAtendimento,
  useCatalogoDeTickets,
  useEquipesDeAtendimento,
  type EstadoDoTicket,
  type Ticket,
} from "../../nucleo/tickets";
import {
  useAlterarEmLote,
  type AlteracoesDoLote,
  type ResultadoDoLote,
} from "../../nucleo/tickets-operacao";
import { Aviso, Botao, Cartao } from "../ui";

const NAO_MUDAR = "";
const LIMPAR = "__limpar";

/** Traduz a escolha de um seletor: vazio não muda, `__limpar` retira, o resto é o valor. */
function escolha(valor: string): string | null | undefined {
  if (valor === NAO_MUDAR) return undefined;
  return valor === LIMPAR ? null : valor;
}

/**
 * Ações em lote sobre a fila: marque vários chamados e mude responsável, equipe, estado,
 * categoria ou serviço de uma vez. É tudo ou nada — se um chamado for barrado, nenhum muda e o
 * relatório diz qual e por quê.
 */
export function AcoesEmLote({ tickets }: { tickets: Ticket[] }) {
  const [aberto, setAberto] = useState(false);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [estado, setEstado] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [equipe, setEquipe] = useState("");
  const [categoria, setCategoria] = useState("");
  const [servico, setServico] = useState("");
  const [resultado, setResultado] = useState<ResultadoDoLote | null>(null);
  const equipes = useEquipesDeAtendimento(aberto);
  const agentes = useAgentesDeAtendimento(aberto);
  const catalogo = useCatalogoDeTickets();
  const alterar = useAlterarEmLote();

  // Só vale o que a fila mostra agora: filtrar depois de marcar não manda chamado escondido.
  const selecionados = tickets.filter((ticket) => marcados.has(ticket.id));
  const categoriaEscolhida = catalogo.categorias.data?.find((item) => item.nome === categoria);
  const servicosDaCategoria = (catalogo.servicos.data ?? []).filter(
    (item) => item.categoria_id === categoriaEscolhida?.id,
  );

  const alteracoes: AlteracoesDoLote = {};
  if (estado) alteracoes.estado = estado as EstadoDoTicket;
  const responsavelEscolhido = escolha(responsavel);
  if (responsavelEscolhido !== undefined) alteracoes.responsavel_id = responsavelEscolhido;
  const equipeEscolhida = escolha(equipe);
  if (equipeEscolhida !== undefined) alteracoes.equipe_id = equipeEscolhida;
  const categoriaFinal = escolha(categoria);
  if (categoriaFinal !== undefined) alteracoes.categoria = categoriaFinal;
  const servicoFinal = escolha(servico);
  if (servicoFinal !== undefined) alteracoes.servico = servicoFinal;
  const temMudanca = Object.keys(alteracoes).length > 0;

  function alternar(id: string) {
    setMarcados((atuais) => {
      const proximos = new Set(atuais);
      if (proximos.has(id)) proximos.delete(id);
      else proximos.add(id);
      return proximos;
    });
  }

  function aplicar(evento: FormEvent) {
    evento.preventDefault();
    setResultado(null);
    alterar.mutate(
      { ticket_ids: selecionados.map((ticket) => ticket.id), alteracoes },
      {
        onSuccess: (retorno) => {
          setResultado(retorno);
          if (retorno.aplicado) {
            setMarcados(new Set());
            setEstado("");
            setResponsavel("");
            setEquipe("");
            setCategoria("");
            setServico("");
          }
        },
      },
    );
  }

  const numeroDe = (id: string) => tickets.find((ticket) => ticket.id === id)?.numero;

  return (
    <Cartao
      titulo="Ações em lote"
      acessorio={
        <Botao variante="fantasma" onClick={() => setAberto((valor) => !valor)} aria-expanded={aberto}>
          {aberto ? "Fechar seleção" : "Selecionar vários"}
        </Botao>
      }
      className="mb-5"
      data-testid="acoes-em-lote"
    >
      {!aberto ? (
        <p className="nota-rodape">Marque vários chamados da fila para atribuir, mover ou classificar de uma vez.</p>
      ) : (
        <form onSubmit={aplicar}>
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={tickets.length > 0 && selecionados.length === tickets.length}
              onChange={(evento) => setMarcados(evento.target.checked ? new Set(tickets.map((t) => t.id)) : new Set())}
            />
            Marcar todos os {tickets.length} da fila
          </label>
          <ul className="lista-de-itens mb-4" aria-label="Chamados para o lote">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={marcados.has(ticket.id)}
                    onChange={() => alternar(ticket.id)}
                    aria-label={`Marcar o chamado ${ticket.numero}`}
                  />
                  <span>#{ticket.numero} · {ticket.assunto}</span>
                  <span className="nota-rodape">{ROTULO_DO_ESTADO[ticket.estado]}</span>
                </label>
              </li>
            ))}
          </ul>

          <div className="grid gap-3 md:grid-cols-3">
            <Seletor rotulo="Mover para" valor={estado} aoMudar={setEstado} padrao="Não mudar">
              {Object.entries(ROTULO_DO_ESTADO).map(([valor, rotulo]) => (
                <option value={valor} key={valor}>{rotulo}</option>
              ))}
            </Seletor>
            <Seletor rotulo="Atribuir a" valor={responsavel} aoMudar={setResponsavel} padrao="Não mudar">
              <option value={LIMPAR}>Sem responsável</option>
              {agentes.data?.map((agente) => <option value={agente.id} key={agente.id}>{agente.nome}</option>)}
            </Seletor>
            <Seletor rotulo="Fila de destino" valor={equipe} aoMudar={setEquipe} padrao="Não mudar">
              <option value={LIMPAR}>Sem fila</option>
              {equipes.data?.map((item) => <option value={item.id} key={item.id}>{item.nome}</option>)}
            </Seletor>
            <Seletor
              rotulo="Classificar como"
              valor={categoria}
              aoMudar={(valor) => {
                setCategoria(valor);
                setServico("");
              }}
              padrao="Não mudar"
            >
              <option value={LIMPAR}>Sem categoria</option>
              {catalogo.categorias.data?.map((item) => <option value={item.nome} key={item.id}>{item.nome}</option>)}
            </Seletor>
            <Seletor
              rotulo="Tipo de atendimento"
              valor={servico}
              aoMudar={setServico}
              padrao="Não mudar"
              desabilitado={!categoriaEscolhida}
            >
              <option value={LIMPAR}>Sem tipo</option>
              {servicosDaCategoria.map((item) => <option value={item.nome} key={item.id}>{item.nome}</option>)}
            </Seletor>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Botao type="submit" disabled={selecionados.length === 0 || !temMudanca || alterar.isPending}>
              {alterar.isPending ? "Aplicando…" : `Aplicar a ${selecionados.length} chamado(s)`}
            </Botao>
            <span className="nota-rodape">Tudo ou nada: se um chamado for barrado, nenhum é alterado.</span>
          </div>
        </form>
      )}

      {alterar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(alterar.error)}</Aviso> : null}
      {resultado?.aplicado ? (
        <Aviso tom="sucesso" className="mt-3" data-testid="resultado-do-lote">
          {resultado.alterados} chamado(s) alterado(s)
          {resultado.sem_mudanca ? `; ${resultado.sem_mudanca} já estava(m) assim` : ""}.
        </Aviso>
      ) : null}
      {resultado && !resultado.aplicado ? (
        <Aviso tom="erro" className="mt-3" data-testid="resultado-do-lote">
          <strong>Nada foi alterado.</strong> {resultado.falhas.length} chamado(s) não puderam entrar no lote:
          <ul className="mt-2">
            {resultado.falhas.map((falha) => {
              const numero = falha.numero ?? numeroDe(falha.ticket_id);
              return (
                <li key={falha.ticket_id}>
                  {numero ? `#${numero}` : "Chamado sem acesso"} · {falha.motivo}
                </li>
              );
            })}
          </ul>
        </Aviso>
      ) : null}
    </Cartao>
  );
}

function Seletor({
  rotulo,
  valor,
  aoMudar,
  padrao,
  desabilitado,
  children,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  padrao: string;
  desabilitado?: boolean;
  children: React.ReactNode;
}) {
  const id = `lote-${rotulo.toLocaleLowerCase("pt-BR").replace(/\s+/g, "-")}`;
  return (
    <label className="campo" htmlFor={id}>
      <span className="campo__rotulo">{rotulo}</span>
      <select
        id={id}
        className="campo__controle"
        value={valor}
        onChange={(evento) => aoMudar(evento.target.value)}
        disabled={desabilitado}
      >
        <option value={NAO_MUDAR}>{padrao}</option>
        {children}
      </select>
    </label>
  );
}
